import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  LogIn, CalendarClock, CalendarPlus, PencilLine, Users, StickyNote, MessageCircle,
  Bell, Camera, ClipboardCheck, Target, Sparkles, Activity, Copy, FileDown, BookOpen, CheckCircle2, ArrowRight,
} from "lucide-react";

const ICONS = [LogIn, CalendarClock, CalendarPlus, PencilLine, Users, StickyNote, MessageCircle, Bell, Camera, ClipboardCheck, Target, Sparkles, Activity, Copy, FileDown, Users];
const LANGS = [{ c: "fr", l: "Français" }, { c: "nl", l: "Nederlands" }, { c: "en", l: "English" }];

export default function GuidePro() {
  const { t, i18n } = useTranslation();
  const steps = t("guidePro.steps", { returnObjects: true }) || [];
  const cur = (i18n.language || "fr").slice(0, 2);

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="guide-pro">
      <div className="rounded-3xl p-8 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0b3d3a 0%, #0f766e 55%, #0d9488 100%)" }}>
        <div className="absolute -right-8 -top-8 h-44 w-44 rounded-full" style={{ background: "radial-gradient(circle, rgba(94,234,212,0.25), transparent 70%)" }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold"><BookOpen className="h-3.5 w-3.5" /> {t("guidePro.badge")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-4">{t("guidePro.title")}</h1>
          <p className="text-white/70 text-sm mt-2 max-w-xl">{t("guidePro.subtitle")}</p>
          <div className="mt-5 flex items-center gap-2" data-testid="guide-lang-switch">
            <span className="text-xs text-white/60 mr-1">{t("guidePro.langLabel")} :</span>
            {LANGS.map((L) => (
              <button key={L.c} onClick={() => i18n.changeLanguage(L.c)} data-testid={`guide-lang-${L.c}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${cur === L.c ? "bg-white text-teal-800" : "bg-white/15 text-white hover:bg-white/25"}`}>
                {L.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {steps.map((s, i) => {
          const Icon = ICONS[i] || BookOpen;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.3) }}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow" data-testid={`guide-step-${i}`}>
              <div className="flex items-start gap-4">
                <div className="relative shrink-0">
                  <div className="h-12 w-12 rounded-2xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg,#0f766e,#0d9488)" }}><Icon className="h-6 w-6" /></div>
                  <div className="absolute -top-2 -left-2 h-6 w-6 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">{s.tag}</div>
                  <h2 className="font-display text-xl font-bold text-slate-900 mt-0.5">{s.title}</h2>
                  <ul className="mt-3 space-y-2">
                    {(s.points || []).map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="h-4 w-4 text-teal-500 shrink-0 mt-0.5" /> <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="rounded-2xl border-2 border-dashed border-teal-200 bg-teal-50/50 p-6 flex items-center gap-3 text-teal-800">
        <ArrowRight className="h-5 w-5 shrink-0" />
        <p className="text-sm font-medium">{t("guidePro.tip")}</p>
      </div>
    </div>
  );
}
